import React, { useState } from 'react';
import { X, FileText, AlertTriangle, Scale, Clock, CreditCard, Globe, Shield } from 'lucide-react';
import Select from 'react-select';

// Uganda regions and districts
const ugandaRegions = [
  {
    label: 'Central',
    options: [
      { value: 'kampala', label: 'Kampala' },
      { value: 'wakiso', label: 'Wakiso' },
      { value: 'mukono', label: 'Mukono' },
      { value: 'mpigi', label: 'Mpigi' },
      { value: 'masaka', label: 'Masaka' },
    ]
  },
  {
    label: 'Eastern',
    options: [
      { value: 'jinja', label: 'Jinja' },
      { value: 'mbale', label: 'Mbale' },
      { value: 'tororo', label: 'Tororo' },
      { value: 'busia', label: 'Busia' },
      { value: 'soroti', label: 'Soroti' },
    ]
  },
  {
    label: 'Northern',
    options: [
      { value: 'gulu', label: 'Gulu' },
      { value: 'lira', label: 'Lira' },
      { value: 'kitgum', label: 'Kitgum' },
      { value: 'arua', label: 'Arua' },
      { value: 'moroto', label: 'Moroto' },
    ]
  },
  {
    label: 'Western',
    options: [
      { value: 'fort_portal', label: 'Fort Portal' },
      { value: 'kabale', label: 'Kabale' },
      { value: 'mbarara', label: 'Mbarara' },
      { value: 'kasese', label: 'Kasese' },
      { value: 'hoima', label: 'Hoima' },
    ]
  }
];

// Languages spoken in Uganda
const languages = [
  { value: 'english', label: 'English' },
  { value: 'luganda', label: 'Luganda' },
  { value: 'swahili', label: 'Swahili' },
  { value: 'runyankole', label: 'Runyankole' },
  { value: 'rukiga', label: 'Rukiga' },
  { value: 'acholi', label: 'Acholi' },
  { value: 'ateso', label: 'Ateso' },
  { value: 'lugbara', label: 'Lugbara' },
  { value: 'lusoga', label: 'Lusoga' },
  { value: 'lumasaba', label: 'Lumasaba' },
  { value: 'french', label: 'French' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'other', label: 'Other' }
];

// Tour specialties
const specialties = [
  { value: 'wildlife_safari', label: 'Wildlife Safari' },
  { value: 'gorilla_trekking', label: 'Gorilla Trekking' },
  { value: 'bird_watching', label: 'Bird Watching' },
  { value: 'cultural_tours', label: 'Cultural Tours' },
  { value: 'city_tours', label: 'City Tours' },
  { value: 'adventure_tours', label: 'Adventure Tours' },
  { value: 'hiking', label: 'Hiking & Trekking' },
  { value: 'photography', label: 'Photography Tours' },
  { value: 'food_tours', label: 'Food & Culinary Tours' },
  { value: 'boat_safaris', label: 'Boat Safaris' },
  { value: 'fishing', label: 'Fishing Experiences' },
  { value: 'community_tourism', label: 'Community Tourism' }
];

interface GuideApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GuideApplicationModal: React.FC<GuideApplicationModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationalId: '',
    address: '',
    city: '',
    region: null,
    bio: '',
    experience: '',
    languages: [],
    specialties: [],
    tourismLicense: false,
    tourismLicenseNumber: '',
    firstAidCertification: false,
    criminalRecord: false,
    profileImage: null,
    idImage: null,
    certificationImages: [],
    tourSamples: '',
    pricing: '',
    availability: '',
    references: '',
    additionalInfo: '',
    termsAccepted: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSelectChange = (selectedOption: any, { name }: { name: string }) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption
    }));
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.nationalId) newErrors.nationalId = 'National ID is required';
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.region) newErrors.region = 'Region is required';
    }

    if (step === 2) {
      if (!formData.bio) newErrors.bio = 'Bio is required';
      if (!formData.experience) newErrors.experience = 'Experience is required';
      if (!formData.languages.length) newErrors.languages = 'At least one language is required';
      if (!formData.specialties.length) newErrors.specialties = 'At least one specialty is required';
    }

    if (step === 3) {
      if (!formData.tourSamples) newErrors.tourSamples = 'Tour samples are required';
      if (!formData.pricing) newErrors.pricing = 'Pricing information is required';
      if (!formData.availability) newErrors.availability = 'Availability information is required';
    }

    if (step === 4) {
      if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      const formBody = {
        'form-name': 'guide-application-form',
        ...formData,
        languages: formData.languages.map(lang => lang.label).join(', '),
        specialties: formData.specialties.map(spec => spec.label).join(', '),
        region: formData.region?.label || ''
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody as any).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
        </div>

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Become a TravelCord Guide</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="flex justify-between">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex flex-col items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      step < currentStep ? 'bg-ta-green text-white' : 
                      step === currentStep ? 'bg-ta-green text-white' : 
                      'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                  <span className="mt-2 text-xs text-gray-500">
                    {step === 1 ? 'Personal Info' : 
                     step === 2 ? 'Experience' : 
                     step === 3 ? 'Services' :
                     'Review'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <form name="guide-application-form" method="POST" data-netlify="true" onSubmit={handleSubmit}>
              <input type="hidden" name="form-name" value="guide-application-form" />
              
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.firstName ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.lastName ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.phone ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date of Birth *</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.dateOfBirth ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="mt-1 text-xs text-red-500">{errors.dateOfBirth}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">National ID *</label>
                      <input
                        type="text"
                        name="nationalId"
                        value={formData.nationalId}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.nationalId ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.nationalId && (
                        <p className="mt-1 text-xs text-red-500">{errors.nationalId}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.address ? 'border-red-500' : ''
                      }`}
                    />
                    {errors.address && (
                      <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                          errors.city ? 'border-red-500' : ''
                        }`}
                      />
                      {errors.city && (
                        <p className="mt-1 text-xs text-red-500">{errors.city}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Region *</label>
                      <Select
                        name="region"
                        value={formData.region}
                        onChange={(option) => handleSelectChange(option, { name: 'region' })}
                        options={ugandaRegions}
                        className={errors.region ? 'border-red-500' : ''}
                        classNamePrefix="select"
                      />
                      {errors.region && (
                        <p className="mt-1 text-xs text-red-500">{errors.region}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Experience */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bio *</label>
                    <textarea
                      name="bio"
                      rows={4}
                      value={formData.bio}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.bio ? 'border-red-500' : ''
                      }`}
                      placeholder="Tell us about yourself..."
                    />
                    {errors.bio && (
                      <p className="mt-1 text-xs text-red-500">{errors.bio}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Experience *</label>
                    <textarea
                      name="experience"
                      rows={4}
                      value={formData.experience}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.experience ? 'border-red-500' : ''
                      }`}
                      placeholder="Describe your tourism/guiding experience..."
                    />
                    {errors.experience && (
                      <p className="mt-1 text-xs text-red-500">{errors.experience}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Languages *</label>
                    <Select
                      isMulti
                      name="languages"
                      value={formData.languages}
                      onChange={(option) => handleSelectChange(option, { name: 'languages' })}
                      options={languages}
                      className={errors.languages ? 'border-red-500' : ''}
                      classNamePrefix="select"
                    />
                    {errors.languages && (
                      <p className="mt-1 text-xs text-red-500">{errors.languages}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Specialties *</label>
                    <Select
                      isMulti
                      name="specialties"
                      value={formData.specialties}
                      onChange={(option) => handleSelectChange(option, { name: 'specialties' })}
                      options={specialties}
                      className={errors.specialties ? 'border-red-500' : ''}
                      classNamePrefix="select"
                    />
                    {errors.specialties && (
                      <p className="mt-1 text-xs text-red-500">{errors.specialties}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="tourismLicense"
                        checked={formData.tourismLicense}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-ta-green focus:ring-ta-green border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        I have a tourism license
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="firstAidCertification"
                        checked={formData.firstAidCertification}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-ta-green focus:ring-ta-green border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        I have first aid certification
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="criminalRecord"
                        checked={formData.criminalRecord}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-ta-green focus:ring-ta-green border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        I have a clean criminal record
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Services */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tour Samples *</label>
                    <textarea
                      name="tourSamples"
                      rows={4}
                      value={formData.tourSamples}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.tourSamples ? 'border-red-500' : ''
                      }`}
                      placeholder="Describe 2-3 sample tours you would offer..."
                    />
                    {errors.tourSamples && (
                      <p className="mt-1 text-xs text-red-500">{errors.tourSamples}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pricing *</label>
                    <textarea
                      name="pricing"
                      rows={3}
                      value={formData.pricing}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.pricing ? 'border-red-500' : ''
                      }`}
                      placeholder="Provide your pricing structure..."
                    />
                    {errors.pricing && (
                      <p className="mt-1 text-xs text-red-500">{errors.pricing}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Availability *</label>
                    <textarea
                      name="availability"
                      rows={3}
                      value={formData.availability}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm ${
                        errors.availability ? 'border-red-500' : ''
                      }`}
                      placeholder="Describe your availability..."
                    />
                    {errors.availability && (
                      <p className="mt-1 text-xs text-red-500">{errors.availability}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">References (Optional)</label>
                    <textarea
                      name="references"
                      rows={3}
                      value={formData.references}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green sm:text-sm"
                      placeholder="List any professional references..."
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Review & Submit */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Please review your information carefully before submitting. Once submitted, you'll receive a confirmation email with next steps.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Application Summary</h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Name</dt>
                        <dd className="text-sm text-gray-900">{formData.firstName} {formData.lastName}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="text-sm text-gray-900">{formData.email}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                        <dd className="text-sm text-gray-900">{formData.phone}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Location</dt>
                        <dd className="text-sm text-gray-900">{formData.city}, {formData.region?.label}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Languages</dt>
                        <dd className="text-sm text-gray-900">{formData.languages.map(lang => lang.label).join(', ')}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Specialties</dt>
                        <dd className="text-sm text-gray-900">{formData.specialties.map(spec => spec.label).join(', ')}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        className={`h-4 w-4 text-ta-green focus:ring-ta-green border-gray-300 rounded ${
                          errors.termsAccepted ? 'border-red-500' : ''
                        }`}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label className="font-medium text-gray-700">Terms and Conditions *</label>
                      <p className="text-gray-500">
                        I agree to TravelCord's Terms of Service and Guide Policies. I confirm that all information provided is accurate and complete.
                      </p>
                      {errors.termsAccepted && (
                        <p className="mt-1 text-xs text-red-500">{errors.termsAccepted}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ta-green"
                >
                  Previous
                </button>
              )}
              
              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="ml-auto px-4 py-2 text-sm font-medium text-white bg-ta-green border border-transparent rounded-md hover:bg-ta-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ta-green"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="ml-auto px-4 py-2 text-sm font-medium text-white bg-ta-green border border-transparent rounded-md hover:bg-ta-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ta-green flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideApplicationModal;